import React, { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ShieldCheck,
  Lock,
  Clock,
  Send,
  HeartHandshake,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SUPPORT_TYPES = [
  { value: "maternal", label: "Mental health / postpartum support" },
  { value: "disability", label: "Raising a child with a disability" },
  { value: "education", label: "Education / return-to-school" },
  { value: "economic", label: "Economic support / livelihood" },
  { value: "health", label: "Maternal or family health" },
  { value: "community", label: "Community / peer support" },
  { value: "other", label: "Something else" },
];

export default function NeedSupport() {
  const [form, setForm] = useState({
    name: "",
    anonymous: false,
    contact_method: "email",
    email: "",
    phone: "",
    location: "",
    support_type: "maternal",
    children_count: "",
    has_child_with_disability: false,
    urgency: "standard",
    situation: "",
    consent: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((f) => ({ ...f, [name]: type === "checkbox" ? checked : value }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email && !form.phone) {
      toast.error("Please share at least one way for us to reach you.");
      return;
    }
    if (!form.situation || form.situation.trim().length < 10) {
      toast.error("Please tell us a little about your situation (at least 10 characters).");
      return;
    }
    if (!form.consent) {
      toast.error("Please confirm consent so we can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        children_count: form.children_count === "" ? null : Number(form.children_count),
        email: form.email || null,
        phone: form.phone || null,
        name: form.anonymous ? null : form.name || null,
      };
      await axios.post(`${API}/help-request`, payload);
      setSubmitted(true);
      toast.success("Tumeipokea — we've received your request. Asante kwa kutuamini.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please check your details and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="need-support"
      data-testid="need-support-section"
      className="relative py-20 lg:py-28 bg-brand-cream"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.22em] text-brand-forest font-semibold">
              Need support?
            </div>
            <h2 className="mt-3 font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-ink leading-[1.05]">
              You don&apos;t have to{" "}
              <span className="italic text-brand-forest">walk this alone.</span>
            </h2>
            <p className="mt-5 text-brand-muted text-lg leading-relaxed max-w-md">
              If you are a mother — or someone caring for a child with a
              disability — please reach out. Tell us what you&apos;re carrying.
              We&apos;ll listen, then connect you with the right circle, program
              or referral.
            </p>

            <div className="mt-8 space-y-4">
              <div
                data-testid="need-support-privacy-card"
                className="rounded-2xl border border-brand-border bg-white p-5"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-brand-forest/10 text-brand-forest flex items-center justify-center">
                    <Lock className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-serif text-lg text-brand-ink">
                      Confidential &amp; safe
                    </div>
                    <div className="text-sm text-brand-muted">
                      Only our care team sees what you share. You can request
                      help anonymously.
                    </div>
                  </div>
                </div>
              </div>
              <div
                data-testid="need-support-time-card"
                className="rounded-2xl border border-brand-border bg-white p-5"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-brand-gold/15 text-brand-gold-dark flex items-center justify-center">
                    <Clock className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-serif text-lg text-brand-ink">
                      Response within 4 weeks
                    </div>
                    <div className="text-sm text-brand-muted">
                      We are a small team and review every request with care.
                      Please allow up to 4 weeks for a reply.
                    </div>
                  </div>
                </div>
              </div>
              <div
                data-testid="need-support-urgent-card"
                className="rounded-2xl border border-brand-border bg-brand-sand/60 p-5"
              >
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-brand-navy/10 text-brand-navy flex items-center justify-center">
                    <ShieldCheck className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="font-serif text-lg text-brand-ink">
                      In immediate danger?
                    </div>
                    <div className="text-sm text-brand-muted">
                      For emergencies in Tanzania call <span className="font-semibold">112</span>. For
                      gender-based violence call the national hotline{" "}
                      <span className="font-semibold">116</span>.
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <motion.div
            className="lg:col-span-7"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            {submitted ? (
              <div
                data-testid="need-support-success"
                className="rounded-[24px] bg-white ring-1 ring-brand-border p-8 lg:p-12 text-center"
              >
                <div className="mx-auto h-16 w-16 rounded-full bg-brand-forest text-white flex items-center justify-center">
                  <HeartHandshake className="h-8 w-8" />
                </div>
                <h3 className="mt-6 font-serif text-2xl lg:text-3xl text-brand-ink">
                  Tumeipokea. We have you.
                </h3>
                <p className="mt-3 text-brand-muted leading-relaxed max-w-md mx-auto">
                  Your request has been received in confidence. Our care team
                  reviews every message personally — please allow up to{" "}
                  <span className="font-semibold text-brand-ink">4 weeks</span>{" "}
                  for a response.
                </p>
                <p className="mt-3 text-sm text-brand-muted">
                  If your situation worsens before we can reach you, please email{" "}
                  <a
                    href="mailto:nymfoundationorg@gmail.com"
                    className="text-brand-forest underline-link font-medium"
                  >
                    nymfoundationorg@gmail.com
                  </a>
                  .
                </p>
                <button
                  type="button"
                  onClick={() => {
                    setSubmitted(false);
                    setForm((f) => ({ ...f, situation: "" }));
                  }}
                  data-testid="need-support-new-request"
                  className="btn-outline mt-8"
                >
                  Submit another request
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="need-support-form"
                className="rounded-[24px] bg-white ring-1 ring-brand-border p-7 lg:p-10"
              >
                <div className="font-serif text-2xl lg:text-3xl text-brand-ink">
                  Tell us how we can walk with you
                </div>
                <p className="mt-2 text-sm text-brand-muted">
                  Every field except where marked is optional. Share only what
                  feels safe.
                </p>

                {/* Anonymous toggle */}
                <label className="mt-6 flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="anonymous"
                    checked={form.anonymous}
                    onChange={change}
                    data-testid="need-support-anonymous-toggle"
                    className="mt-1 h-5 w-5 rounded border-brand-border accent-brand-forest"
                  />
                  <span className="text-sm text-brand-ink">
                    I&apos;d like to remain anonymous (you can still share a contact method below).
                  </span>
                </label>

                <div className="mt-5 grid sm:grid-cols-2 gap-4">
                  {!form.anonymous && (
                    <div className="sm:col-span-2">
                      <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                        Your name (optional)
                      </label>
                      <input
                        name="name"
                        value={form.name}
                        onChange={change}
                        data-testid="need-support-name-input"
                        className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                        placeholder="What should we call you?"
                      />
                    </div>
                  )}

                  <div>
                    <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                      Email
                    </label>
                    <input
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={change}
                      data-testid="need-support-email-input"
                      className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                      placeholder="you@email.com"
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                      Phone / WhatsApp
                    </label>
                    <input
                      name="phone"
                      value={form.phone}
                      onChange={change}
                      data-testid="need-support-phone-input"
                      className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                      placeholder="+255 ..."
                    />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                      Best way to reach you
                    </label>
                    <select
                      name="contact_method"
                      value={form.contact_method}
                      onChange={change}
                      data-testid="need-support-contact-method-select"
                      className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                    >
                      <option value="email">Email</option>
                      <option value="phone">Phone call</option>
                      <option value="whatsapp">WhatsApp</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                      Where are you based?
                    </label>
                    <input
                      name="location"
                      value={form.location}
                      onChange={change}
                      data-testid="need-support-location-input"
                      placeholder="e.g. Dar es Salaam, Kinondoni"
                      className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                    />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                      What kind of support do you need?
                    </label>
                    <select
                      name="support_type"
                      value={form.support_type}
                      onChange={change}
                      data-testid="need-support-type-select"
                      className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                    >
                      {SUPPORT_TYPES.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                      Number of children (optional)
                    </label>
                    <input
                      name="children_count"
                      type="number"
                      min="0"
                      max="20"
                      value={form.children_count}
                      onChange={change}
                      data-testid="need-support-children-input"
                      className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                      placeholder="e.g. 2"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-start gap-3 cursor-pointer w-full pb-3">
                      <input
                        type="checkbox"
                        name="has_child_with_disability"
                        checked={form.has_child_with_disability}
                        onChange={change}
                        data-testid="need-support-disability-checkbox"
                        className="mt-1 h-5 w-5 rounded border-brand-border accent-brand-forest"
                      />
                      <span className="text-sm text-brand-ink">
                        I am raising a child with a disability.
                      </span>
                    </label>
                  </div>
                </div>

                <div className="mt-4">
                  <label className="text-xs uppercase tracking-[0.16em] text-brand-muted font-semibold">
                    Briefly, what are you carrying right now?
                  </label>
                  <textarea
                    name="situation"
                    value={form.situation}
                    onChange={change}
                    data-testid="need-support-situation-input"
                    required
                    rows={5}
                    minLength={10}
                    maxLength={4000}
                    className="mt-2 w-full rounded-xl border border-brand-border bg-brand-cream/60 px-4 py-3 outline-none focus:ring-2 focus:ring-brand-forest/40 focus:border-brand-forest text-brand-ink"
                    placeholder="Share whatever feels safe — even a few sentences are enough."
                  />
                  <div className="mt-1 text-xs text-brand-muted">
                    {form.situation.length}/4000
                  </div>
                </div>

                <label className="mt-5 flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    name="consent"
                    checked={form.consent}
                    onChange={change}
                    data-testid="need-support-consent-checkbox"
                    className="mt-1 h-5 w-5 rounded border-brand-border accent-brand-forest"
                  />
                  <span className="text-sm text-brand-ink">
                    I consent for the Nguvu Ya Mwanamke Foundation care team to
                    reach me using the contact information above. I understand a
                    reply may take up to 4 weeks.
                  </span>
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="need-support-submit-button"
                  className="btn-primary mt-6 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? "Sending..." : "Send my request"}
                  <Send className="h-4 w-4" />
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
